"use client";

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { API_ENDPOINTS } from "../../../constants/config";
import { ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";

function AddWing({ onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    try {
      setError(null);
      if (!name?.trim()) {
        setError("Wing name is required");
        return;
      }
      await axiosInstance.post(API_ENDPOINTS.wings, { name: name.trim() });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add wing");
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Add Wing</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Wing name" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Add Wing</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditWing({ wing, onClose, onSuccess }) {
  const [name, setName] = useState(wing?.name || "");
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    try {
      setError(null);
      if (!name?.trim()) {
        setError("Wing name is required");
        return;
      }
      await axiosInstance.patch(API_ENDPOINTS.wing(wing._id), { name: name.trim() });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update wing");
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Edit Wing</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Wing name" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Update</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function WingManagement() {
  const navigate = useNavigate();
  const [wings, setWings] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedWing, setSelectedWing] = useState(null);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const getSortIcon = (field) => {
    if (field !== sortField) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    return sortDirection === "asc"
      ? <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
      : <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />;
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSorted = () => {
    if (!sortField) return wings;
    return [...wings].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (sortField === "createdAt") {
        va = new Date(va).getTime();
        vb = new Date(vb).getTime();
      }
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDirection === "asc" ? -1 : 1;
      if (va > vb) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const fetchWings = useCallback(async () => {
    try {
      setError(null);
      const res = await axiosInstance.get(API_ENDPOINTS.wings);
      setWings(res.data?.data?.wings || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch wings");
    }
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this wing?")) return;
    try {
      setError(null);
      await axiosInstance.delete(API_ENDPOINTS.wing(id));
      fetchWings();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete wing");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }, []);

  useEffect(() => {
    fetchWings();
  }, [fetchWings]);

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchWings}>Refresh List</Button>
            <Button variant="outline" onClick={() => navigate("/dashboard/settings/wing-mapping")}>
              Teacher/Staff Mapping
            </Button>
          </div>
          <Button onClick={() => setShowAddModal(true)}>Add Wing</Button>
        </div>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold text-gray-600 cursor-pointer" onClick={() => handleSort("name")}>
                <div className="flex items-center">Name {getSortIcon("name")}</div>
              </TableHead>
              <TableHead className="font-semibold text-gray-600 cursor-pointer" onClick={() => handleSort("createdAt")}>
                <div className="flex items-center">Created {getSortIcon("createdAt")}</div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {getSorted().map((w) => (
              <TableRow key={w._id}>
                <TableCell>{w.name}</TableCell>
                <TableCell>{new Date(w.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setSelectedWing(w); setShowEditModal(true); }}>Edit</Button>
                    <Button variant="destructive" onClick={() => handleDelete(w._id)}>Delete</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {showAddModal && (
          <div className="mt-4">
            <AddWing onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); fetchWings(); }} />
          </div>
        )}
        {showEditModal && selectedWing && (
          <div className="mt-4">
            <EditWing
              wing={selectedWing}
              onClose={() => { setShowEditModal(false); setSelectedWing(null); }}
              onSuccess={() => { setShowEditModal(false); setSelectedWing(null); fetchWings(); }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
